import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  BookOpen, 
  CheckCircle,
  Eye
} from 'lucide-react';
import axios from 'axios';

// Función para reproducir sonido de celebración festiva
const playCelebrationSound = () => {
  try {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    
    // Crear múltiples capas de sonido para una celebración más rica
    const createTone = (freq: number, startTime: number, duration: number, volume: number, type: OscillatorType = 'sine') => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.setValueAtTime(freq, audioContext.currentTime + startTime);
      oscillator.type = type;
      
      gainNode.gain.setValueAtTime(0, audioContext.currentTime + startTime);
      gainNode.gain.linearRampToValueAtTime(volume, audioContext.currentTime + startTime + 0.05);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + startTime + duration);
      
      oscillator.start(audioContext.currentTime + startTime);
      oscillator.stop(audioContext.currentTime + startTime + duration);
    };
    
    // Melodía principal de celebración (más alegre y festiva)
    const celebrationMelody = [
      { freq: 523.25, time: 0, duration: 0.4 },    // C
      { freq: 659.25, time: 0.2, duration: 0.4 },  // E
      { freq: 783.99, time: 0.4, duration: 0.4 },   // G
      { freq: 1046.50, time: 0.6, duration: 0.8 },  // C alto
      { freq: 783.99, time: 0.8, duration: 0.4 },   // G
      { freq: 1046.50, time: 1.0, duration: 0.8 },  // C alto
      { freq: 1318.51, time: 1.2, duration: 1.0 }  // E alto (nota final alta)
    ];
    
    // Reproducir melodía principal
    celebrationMelody.forEach(note => {
      createTone(note.freq, note.time, note.duration, 0.12, 'sine');
    });
    
    // Agregar armonías (notas más bajas para darle cuerpo)
    setTimeout(() => {
      createTone(261.63, 0, 1.5, 0.08, 'triangle'); // C bajo
      createTone(329.63, 0.3, 1.2, 0.06, 'triangle'); // E bajo
      createTone(392.00, 0.6, 1.0, 0.07, 'triangle'); // G bajo
    }, 100);
    
    // Efecto de "fanfare" al final (como trompetas)
    setTimeout(() => {
      createTone(1046.50, 0, 0.3, 0.15, 'square'); // C alto
      createTone(1318.51, 0.1, 0.3, 0.15, 'square'); // E alto
      createTone(1567.98, 0.2, 0.5, 0.2, 'square'); // G alto
    }, 1500);
    
    // Sonido de "pop" festivo final
    setTimeout(() => {
      const popOscillator = audioContext.createOscillator();
      const popGain = audioContext.createGain();
      
      popOscillator.connect(popGain);
      popGain.connect(audioContext.destination);
      
      popOscillator.frequency.setValueAtTime(1200, audioContext.currentTime);
      popOscillator.frequency.exponentialRampToValueAtTime(200, audioContext.currentTime + 0.5);
      popOscillator.type = 'square';
      
      popGain.gain.setValueAtTime(0.25, audioContext.currentTime);
      popGain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      
      popOscillator.start(audioContext.currentTime);
      popOscillator.stop(audioContext.currentTime + 0.5);
    }, 2000);
    
  } catch (error) {
    console.log('No se pudo reproducir el sonido:', error);
  }
};

// Componente de confeti
const Confetti: React.FC = () => {
  const confettiPieces = Array.from({ length: 50 }, (_, i) => (
    <motion.div
      key={i}
      initial={{ 
        x: Math.random() * window.innerWidth,
        y: -10,
        rotate: 0,
        scale: Math.random() * 0.5 + 0.5
      }}
      animate={{ 
        y: window.innerHeight + 100,
        rotate: 360,
        x: Math.random() * window.innerWidth
      }}
      transition={{ 
        duration: Math.random() * 3 + 2,
        ease: "easeOut"
      }}
      style={{
        position: 'fixed',
        width: '10px',
        height: '10px',
        background: ['#ff6b6b', '#4ecdc4', '#45b7d1', '#ffd700', '#ff9ff3', '#54a0ff'][Math.floor(Math.random() * 6)],
        borderRadius: '50%',
        zIndex: 1000,
        pointerEvents: 'none'
      }}
    />
  ));

  return <>{confettiPieces}</>;
};

interface PlacementResultsData {
  startersScore: number;
  moversScore: number;
  flyersScore: number;
  assignedLevel: string;
  levelDescription: string;
  totalQuestions: number;
}

const PlacementResults: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [results, setResults] = useState<PlacementResultsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [showConfetti, setShowConfetti] = useState(true);

  const fetchResults = React.useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('/placement/results');
      setResults(response.data.results);
    } catch (error) {
      console.error('Error fetching results:', error);
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (location.state?.results) {
      setResults(location.state.results);
    } else {
      // Si no hay resultados en el state, intentar obtenerlos del servidor
      fetchResults();
    }
    
    // Reproducir sonido de celebración cuando se muestre el confeti
    if (showConfetti) {
      playCelebrationSound();
    }
    
    // Detener el confeti después de 5 segundos
    const confettiTimer = setTimeout(() => {
      setShowConfetti(false);
    }, 5000);
    
    return () => clearTimeout(confettiTimer);
  }, [location.state, fetchResults, showConfetti]);

  const getLevelInfo = (level: string) => {
    if (level.includes('A2')) {
      return { 
        emoji: '🌟', 
        color: '#45b7d1', 
        bgColor: '#45b7d120',
        message: '¡Increíble! 🌟 Tu inglés se entiende muy bien. Sigue así y llegarás más lejos.',
        title: 'A2 – ¡Gran avance!',
        description: 'Puedes contar experiencias breves, describir objetos y lugares, y usar conectores simples. Próximo paso: ampliar vocabulario y usar pasado simple en tus relatos.',
        reinforcement: [
          'Practica contar tu día en pasado.',
          'Describe un lugar con 3 adjetivos.',
          'Aprende 5 verbos nuevos esta semana.'
        ]
      };
    } else if (level.includes('A1')) {
      return { 
        emoji: '🎯', 
        color: '#4ecdc4', 
        bgColor: '#4ecdc420',
        message: '¡Fantástico! 🎯 Tu inglés suena claro. Practica un poco cada día para seguir avanzando.',
        title: 'A1 – ¡Excelente progreso!',
        description: 'Comprendes y usas frases sencillas en situaciones cotidianas (presentarte, gustos, familia). Próximo paso: preguntas cortas y respuestas rápidas con “Wh-”.',
        reinforcement: [
          'Practica saludos y presentaciones.',
          'Haz preguntas cortas (What/Where/When).',
          'Repite frases útiles de tu día a día.'
        ]
      };
    } else if (level.includes('Pre-A1')) {
      return { 
        emoji: '🚀', 
        color: '#ff6b6b', 
        bgColor: '#ff6b6b20',
        message: '¡Buen comienzo! 🚀 Estás empezando tu aventura en inglés. Cada práctica suma.',
      title: 'Pre-A1 – ¡Buen comienzo!',
      description: 'Reconoces palabras y expresiones muy frecuentes: saludos, números y colores. Próximo paso: formar frases cortas con "I am", "I like" y "This is".',
      reinforcement: [
        'Aprende 5 palabras nuevas hoy.',
        'Escucha una canción corta en inglés.',
        'Di 3 frases con "I am / I like / This is".'
        ]
      };
    } else if (level.includes('Por debajo de Pre-A1') || level.includes('debajo')) {
      return { 
        emoji: '📚', 
        color: '#9b59b6', 
        bgColor: '#9b59b620',
        message: '¡Bienvenido! 📚 Todos empezamos desde el principio. Con dedicación y práctica constante, desarrollarás tus habilidades paso a paso.',
        title: 'Por debajo de Pre-A1 – ¡Tu aventura comienza aquí!',
        description: 'Estás comenzando tu viaje en el aprendizaje del inglés. Cada paso cuenta y cada práctica te acerca más a tus objetivos. ¡No te rindas!',
        reinforcement: [
          'Aprende 3 palabras nuevas cada día.',
          'Escucha saludos básicos en inglés.',
          'Practica los números del 1 al 10.'
        ]
      };
    } else {
      // Caso por defecto para cualquier otro nivel no reconocido
      return { 
        emoji: '📚', 
        color: '#666', 
        bgColor: '#66620',
        message: '¡Genial! 📚 Tu camino en el inglés está en marcha. Mantén el ritmo.',
        title: level || '¡Seguimos avanzando!',
        description: 'Muy pronto verás tu siguiente objetivo con actividades a tu medida. Practicar un poco cada día hace la diferencia.',
        reinforcement: [
          'Repite tus frases favoritas.',
          'Escucha una mini-historia sencilla.'
        ]
      };
    }
  };

  const getGrade = (percentage: number) => {
    if (percentage >= 90) return { grade: 'A+', color: '#56ab2f' };
    if (percentage >= 80) return { grade: 'A', color: '#4ecdc4' };
    if (percentage >= 70) return { grade: 'B', color: '#ffd700' };
    if (percentage >= 60) return { grade: 'C', color: '#ff6b6b' };
    return { grade: 'D', color: '#ff416c' };
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p style={{ marginTop: '20px', color: 'white', fontSize: '18px' }}>
          Cargando tus resultados... 🎯
        </p>
      </div>
    );
  }

  if (!results) {
    return (
      <div className="container">
        <div className="card" style={{ textAlign: 'center', maxWidth: '500px', margin: '50px auto' }}>
          <h2>😔 No se encontraron resultados</h2>
          <p>No se pudieron cargar los resultados de tu examen de ubicación.</p>
          <button onClick={() => navigate('/dashboard')} className="btn btn-secondary">
            Volver al Dashboard
          </button>
        </div>
      </div>
    );
  }

  const levelInfo = getLevelInfo(results.assignedLevel);
  const totalScore = results.startersScore + results.moversScore + results.flyersScore;
  const percentage = Math.round((totalScore / 20) * 100);
  const gradeInfo = getGrade(percentage);

  return (
    <div className="container">
      {/* Confeti */}
      {showConfetti && <Confetti />}
      
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="title"
      >
        <h1>
          <span className="emoji">🎉</span>
          ¡¡¡FELICIDADES!!! ¡¡¡LO HICISTE GENIAL!!!
          <span className="emoji">🎊</span>
        </h1>
        <h2>¡Eres súper inteligente! ¡Tu inglés está increíble!</h2>
      </motion.div>

      {/* Resultado principal */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.2 }}
        className="card"
        style={{ 
          background: `linear-gradient(45deg, ${levelInfo.bgColor}, rgba(255,255,255,0.9))`,
          border: `3px solid ${levelInfo.color}`,
          textAlign: 'center',
          marginBottom: '30px'
        }}
      >
        <div style={{ marginBottom: '30px' }}>
          <div style={{ fontSize: '5rem', marginBottom: '25px' }}>
            {levelInfo.emoji}
          </div>
          <h2 style={{ color: '#333', marginBottom: '15px', fontSize: '3rem', fontWeight: 'bold' }}>
            {levelInfo.title}
          </h2>
          <p style={{ color: '#333', fontSize: '22px', marginBottom: '25px', fontWeight: '600', lineHeight: '1.4' }}>
            {levelInfo.description}
          </p>
        </div>

        <div className="message" style={{ 
          background: `linear-gradient(45deg, ${levelInfo.bgColor}, rgba(255,255,255,0.9))`,
          border: `2px solid ${levelInfo.color}`,
          color: '#333',
          fontSize: '20px',
          fontWeight: '600',
          padding: '20px',
          borderRadius: '15px'
        }}>
          {levelInfo.message}
        </div>
      </motion.div>

      {/* Estadísticas detalladas */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="grid grid-3"
      >
        <div className="card" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: '10px' }}>🚀</div>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#ff6b6b', marginBottom: '5px' }}>
            {results.startersScore}/7
          </div>
          <div style={{ color: '#333', marginBottom: '10px', fontSize: '18px', fontWeight: '600' }}>Pre-A1</div>
          <div style={{ 
            background: '#56ab2f', 
            color: 'white', 
            padding: '5px 10px', 
            borderRadius: '15px', 
            fontSize: '12px',
            fontWeight: 'bold'
          }}>
            ✅ ¡Súper Bien!
          </div>
        </div>

        <div className="card" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: '10px' }}>🎯</div>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#4ecdc4', marginBottom: '5px' }}>
            {results.moversScore}/7
          </div>
          <div style={{ color: '#333', marginBottom: '10px', fontSize: '18px', fontWeight: '600' }}>A1</div>
          <div style={{ 
            background: '#56ab2f', 
            color: 'white', 
            padding: '5px 10px', 
            borderRadius: '15px', 
            fontSize: '12px',
            fontWeight: 'bold'
          }}>
            ✅ ¡Genial!
          </div>
        </div>

        <div className="card" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: '10px' }}>🌟</div>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#45b7d1', marginBottom: '5px' }}>
            {results.flyersScore}/6
          </div>
          <div style={{ color: '#333', marginBottom: '10px', fontSize: '18px', fontWeight: '600' }}>A2</div>
          <div style={{ 
            background: '#56ab2f', 
            color: 'white', 
            padding: '5px 10px', 
            borderRadius: '15px', 
            fontSize: '12px',
            fontWeight: 'bold'
          }}>
            ✅ ¡Increíble!
          </div>
        </div>
      </motion.div>

      {/* Botones de acción */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        style={{ textAlign: 'center', marginTop: '30px', marginBottom: '30px' }}
      >
        <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={() => navigate('/placement/test/review')}
            className="btn"
            style={{ 
              fontSize: '18px', 
              padding: '15px 30px',
              background: 'linear-gradient(45deg, #2262d3, #4a7cff)'
            }}
          >
            <BookOpen style={{ marginRight: '8px' }} />
            📚 Ver Test
          </button>
          <button
            onClick={() => window.location.href = 'https://children.ibcidiomas.com'}
            className="btn btn-secondary"
            style={{ 
              fontSize: '18px', 
              padding: '15px 30px'
            }}
          >
            <BookOpen style={{ marginRight: '8px' }} />
            Continuar Aprendiendo
          </button>
        </div>

        <div style={{ 
          marginTop: '20px', 
          padding: '15px', 
          background: 'rgba(78, 205, 196, 0.1)', 
          borderRadius: '10px',
          color: '#333'
        }}>
          <CheckCircle style={{ display: 'inline', marginRight: '8px', color: '#4ecdc4' }} />
          <strong>¡Recibirás un email súper especial con tus resultados!</strong>
          <br />
          <small style={{ color: '#333' }}>¡Mira tu bandeja de entrada para tu certificado genial!</small>
        </div>
      </motion.div>

      {/* Resumen de puntuación */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="card"
        style={{ textAlign: 'center' }}
      >
        <h3 style={{ color: '#333', marginBottom: '20px', fontSize: '24px', fontWeight: 'bold' }}>
          🏆 ¡Tu Puntuación Súper Genial!
        </h3>
        
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
          gap: '20px',
          marginBottom: '20px'
        }}>
          <div>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#4ecdc4' }}>
              {totalScore}/20
            </div>
            <div style={{ color: '#333', fontSize: '16px', fontWeight: '600' }}>Puntos Totales</div>
          </div>
          
          <div>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: gradeInfo.color }}>
              {percentage}%
            </div>
            <div style={{ color: '#333', fontSize: '16px', fontWeight: '600' }}>Porcentaje</div>
          </div>
        </div>

        <div style={{ 
          background: 'rgba(255, 255, 255, 0.9)', 
          padding: '25px', 
          borderRadius: '20px',
          marginTop: '20px',
          border: `3px solid ${levelInfo.color}`
        }}>
          <h4 style={{ color: '#333', marginBottom: '20px', fontSize: '24px', fontWeight: 'bold' }}>
            🌟 ¡Eres Súper Especial!
          </h4>
          <p style={{ color: '#333', margin: '0 0 20px 0', lineHeight: '1.6', fontSize: '18px', fontWeight: '500' }}>
            {levelInfo.description}
          </p>
          
          <h5 style={{ color: '#333', marginBottom: '15px', fontSize: '20px', fontWeight: 'bold' }}>
            🚀 ¡Para Ser Aún Más Genial!
          </h5>
          <ul style={{ color: '#333', margin: 0, paddingLeft: '20px', fontSize: '16px', lineHeight: '1.6' }}>
            {levelInfo.reinforcement.map((item: string, index: number) => (
              <li key={index} style={{ marginBottom: '8px', fontWeight: '500' }}>
                {item}
              </li>
            ))}
          </ul>
        </div>
      </motion.div>

      {/* Elementos decorativos */}
      <motion.div
        animate={{ 
          y: [0, -20, 0],
          rotate: [0, 10, 0]
        }}
        transition={{ 
          duration: 3,
          repeat: Infinity,
          ease: "easeInOut"
        }}
        style={{
          position: 'absolute',
          top: '10%',
          right: '5%',
          fontSize: '3rem',
          opacity: 0.6
        }}
      >
        🎉
      </motion.div>

      <motion.div
        animate={{ 
          y: [0, 25, 0],
          rotate: [0, -10, 0]
        }}
        transition={{ 
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut"
        }}
        style={{
          position: 'absolute',
          bottom: '20%',
          left: '5%',
          fontSize: '2.5rem',
          opacity: 0.7
        }}
      >
        🏆
      </motion.div>

      <motion.div
        animate={{ 
          scale: [1, 1.2, 1],
          rotate: [0, 360, 0]
        }}
        transition={{ 
          duration: 6,
          repeat: Infinity,
          ease: "easeInOut"
        }}
        style={{
          position: 'absolute',
          top: '20%',
          left: '10%',
          fontSize: '2rem',
          opacity: 0.5
        }}
      >
        ⭐
      </motion.div>

      <motion.div
        animate={{ 
          y: [0, -15, 0],
          x: [0, 10, 0]
        }}
        transition={{ 
          duration: 5,
          repeat: Infinity,
          ease: "easeInOut"
        }}
        style={{
          position: 'absolute',
          bottom: '10%',
          right: '10%',
          fontSize: '2.5rem',
          opacity: 0.6
        }}
      >
        🎊
      </motion.div>
    </div>
  );
};

export default PlacementResults;